import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import moment from 'moment';
import { appId } from '@/config/index.js';
import { getDevice } from '@/utils/getdevice.js';
import { getAccessToken, getUserInfo } from '@/api/account';
import { wxConfig } from '@/utils/wxinit';
import { reportLoginPlatform, reportLoginForum } from '@/api/report';
import { Dialog } from 'vant';
import { isWeiXin } from '@/utils';

Vue.use(Router);

const forumKeepAliveMeta = { keepAlive: true };

const getRouteSnapshot = (route) => ({
  name: route.name,
  query: { ...(route.query || {}) },
  params: { ...(route.params || {}) },
});

const isForumRoute = (route) => {
  const routeName = typeof route.name === 'string' ? route.name : '';
  const routePath = route.path || '';
  return routeName.indexOf('forum-') === 0 || routePath === '/forum' || routePath.indexOf('/forum/') === 0;
};

const syncForumRouteTitle = (route) => {
  if (!isForumRoute(route) || !route.meta || !route.meta.title) return;
  document.title = route.meta.title;
};

const openForumBindAccount = (route) => {
  const appState = store.state.app || {};
  if (appState.showBindAccountDialog) return;
  if (!appState.bindAccountRefreshRoute) {
    store.commit('app/SET_BIND_ACCOUNT_REFRESH_ROUTE', getRouteSnapshot(route));
  }
  store.commit('app/CHANGE_BIND_ACCOUNT_DIALOG', true);
};

const ensureForumUidForRoute = (route) => {
  if (!isForumRoute(route)) return;
  const { userInfo } = store.state.account;
  if (!userInfo || userInfo.game_user_id) return;
  openForumBindAccount(route);
};

const ensureForumUid = (to, from, next) => {
  ensureForumUidForRoute(to);
  next();
};

const router = new Router({
  base: process.env.BASE_URL,
  routes: [
    // ———————————————————————————————————— 黑喵论坛页面 ———————————————————————————————————————
    // 游戏圈
    {
      path: '/gameCircle',
      name: 'gameCircle',
      component: () => import('@/views/gameCircle/index.vue'),
      children: [
        // 游戏圈帖子列表页
        {
          path: 'home',
          name: 'gameCircle.home',
          component: () => import('@/views/gameCircle/home.vue'),
          meta: { title: '游戏圈', bottomNav: true, keepAlive: true, isBack: false },
        },
        // 帖子详情页
        {
          path: 'details',
          name: 'gameCircle.details',
          component: () => import('@/views/gameCircle/details.vue'),
          meta: { title: '帖子详情' },
        },
      ],
    },
    // ———————————————————————————————————— 黑喵平台页面 ———————————————————————————————————————
    // 论坛注册页
    {
      path: '/account/registeredAccount',
      name: 'account.registeredAccount',
      component: () => import('@/views/account/registeredAccount.vue'),
    },
    // ———————————————————————————————————— 统一论坛页面 ———————————————————————————————————————
    // 论坛广场
    {
      path: '/forum/square',
      name: 'forum-square',
      component: () => import('@/views/forum/Square.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '论坛' },
    },
    // 论坛首页
    {
      path: '/forum/group/:groupId',
      name: 'forum-group',
      component: () => import('@/views/forum/GroupHome.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '论坛' },
    },
    // 发帖：静态路由必须在 /forum/post/:postId 前，避免 create 被当作 postId
    {
      path: '/forum/post/create',
      name: 'forum-post-create',
      component: () => import('@/views/forum/PostCreate.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '发帖' },
    },
    // 帖子详情
    {
      path: '/forum/post/:postId',
      name: 'forum-post-detail',
      component: () => import('@/views/forum/PostDetail.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '帖子详情' },
    },
    // 帖子集合页
    {
      path: '/forum/assemble/:assembleId',
      name: 'forum-assemble',
      component: () => import('@/views/forum/PostAssemble.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '帖子集合' },
    },
    // 个人主页
    {
      path: '/forum/profile',
      name: 'forum-profile',
      component: () => import('@/views/forum/Profile.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '个人主页' },
    },
    // 编辑资料
    {
      path: '/forum/edit-profile',
      name: 'forum-edit-profile',
      component: () => import('@/views/forum/EditProfile.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '编辑资料' },
    },
    // 消息中心
    {
      path: '/forum/messages',
      name: 'forum-messages',
      component: () => import('@/views/forum/Messages.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '消息中心' },
    },
    // 搜索页：通过 query.groupId 保持当前论坛范围
    {
      path: '/forum/search',
      name: 'forum-search',
      component: () => import('@/views/forum/Search.vue'),
      beforeEnter: ensureForumUid,
      meta: { ...forumKeepAliveMeta, title: '搜索' },
    },
    // 游戏圈中心页
    {
      path: '/gameCircleCenter',
      name: 'gameCircleCenter',
      component: () => import('@/views/gameCircleCenter/index.vue'),
      children: [
        {
          path: 'home',
          name: 'gameCircleCenter.home',
          component: () => import('@/views/gameCircleCenter/home.vue'),
          meta: { title: '游戏圈中心', bottomNav: true },
        },
      ],
    },
    // 个人主页
    {
      path: '/personalCenter',
      name: 'personalCenter',
      component: () => import('@/views/personalCenter/index.vue'),
      children: [
        {
          path: 'home',
          name: 'personalCenter.home',
          component: () => import('@/views/personalCenter/home.vue'),
          meta: { title: '个人主页', bottomNav: true },
        },
        {
          path: 'modifyInfo',
          name: 'personalCenter.modifyInfo',
          component: () => import('@/views/personalCenter/modifyInfo.vue'),
          meta: { title: '黑喵精灵', bottomNav: true },
        },
        {
          path: 'myNews',
          name: 'personalCenter.myNews',
          component: () => import('@/views/personalCenter/myNews.vue'),
          meta: { title: '我的消息', bottomNav: true },
        },
        {
          path: 'myGift',
          name: 'personalCenter.myGift',
          component: () => import('@/views/personalCenter/myGift.vue'),
          meta: { title: '我的礼包', bottomNav: true },
        },
      ],
    },
    // 论坛公告页面
    {
      path: '/account/announcement',
      name: 'account.announcement',
      component: () => import('@/views/account/announcement.vue'),
    },
    // 游戏中心页
    {
      path: '/gameCenter',
      name: 'gameCenter',
      redirect: '/gameCenter/home',
      component: () => import('@/views/gameCenter/index.vue'),
      children: [
        {
          path: 'home',
          name: 'gameCenter.home',
          component: () => import('@/views/gameCenter/home2.vue'),
          meta: { title: '游戏中心', bottomNav: true, suspend: 1 },
        },
      ],
    },
    // 礼包中心页
    {
      path: '/giftCenter',
      name: 'giftCenter',
      redirect: '/giftCenter/home',
      component: () => import('@/views/giftCenter/index.vue'),
      children: [
        {
          path: 'home',
          name: 'giftCenter.home',
          component: () => import('@/views/giftCenter/home.vue'),
          meta: { title: '礼包中心', keepAlive: true, suspend: 2 },
        },
        {
          path: 'activityBridge',
          name: 'giftCenter.activityBridge',
          component: () => import('@/views/giftCenter/activityBridge.vue'),
          meta: { title: '活动中心' },
        },
        {
          path: 'details',
          name: 'giftCenter.details',
          component: () => import('@/views/giftCenter/details.vue'),
          meta: { title: '礼包详情' },
        },
        {
          path: 'activatedGame',
          name: 'giftCenter.activatedGame',
          component: () => import('@/views/giftCenter/activatedGame.vue'),
          meta: { title: '已激活游戏' },
        },
        {
          path: 'allGift',
          name: 'giftCenter.allGift',
          component: () => import('@/views/giftCenter/allGift.vue'),
          meta: { title: '全部礼包' },
        },
      ],
    },
    // 活动页
    {
      path: '/activity',
      name: 'activity',
      component: () => import('@/views/activity/index.vue'),
      children: [
        {
          // 签到有礼活动
          path: 'signGift',
          name: 'activity.signGift',
          component: () => import('@/views/activity/signGift.vue'),
          meta: { title: '签到有礼' },
        },
        {
          // 代金券
          path: 'vouchers',
          name: 'activity.vouchers',
          component: () => import('@/views/activity/vouchers.vue'),
          meta: { title: '代金券', bottomNav: true },
        },
        {
          // 活动跳转页面
          path: 'authorization',
          name: 'activity.authorization',
          component: () => import('@/views/activity/authorization.vue'),
          meta: { title: '代金券大回馈' },
        },
        {
          // 秒杀活动
          path: 'seckill',
          name: 'activity.seckill',
          component: () => import('@/views/activity/seckill.vue'),
          redirect: (to) => {
            const seckillActHost = ['hm.yuwanf.com', 'hm.hnyuezan7.cn'];
            if (seckillActHost.includes(window.location.hostname)) {
              return 'seckillAct';
            } else {
              return 'authorization';
            }
          },
          meta: { title: '代金券大回馈' },
        },
        {
          // 秒杀活动真实路径
          path: 'seckillAct',
          name: 'activity.seckillAct',
          component: () => import('@/views/activity/seckill.vue'),
          meta: { title: '代金券大回馈' },
        },
        {
          // 秒杀活动规则页面
          path: 'seckillRules',
          name: 'activity.seckillRules',
          component: () => import('@/views/activity/components/seckill/seckillRules.vue'),
          meta: { title: '代金券大回馈' },
        },
        {
          // 代金券使用教程页面
          path: 'userGuidance',
          name: 'activity.userGuidance',
          component: () => import('@/views/activity/components/seckill/userGuidance.vue'),
          meta: { title: '代金券大回馈' },
        },
        {
          // 871帝王荣耀签到抽奖活动
          path: 'imperialGlory',
          name: 'activity.imperialGlory',
          component: () => import('@/views/activity/imperialGlory.vue'),
          meta: { title: '帝王荣耀签到抽奖' },
        },
        {
          // 947逍遥三国拉收活动
          path: 'freeKingdoms',
          name: 'activity.freeKingdoms',
          component: () => import('@/views/activity/freeKingdoms.vue'),
          meta: { title: '逍遥三国' },
        },
        {
          // 1001傲世龙城平台活动
          path: 'dragonCity',
          name: 'activity.dragonCity',
          component: () => import('@/views/activity/dragonCity.vue'),
          meta: { title: '傲世龙城' },
        },
        {
          // 2025.07.02 转游开红包
          path: 'retourGame',
          name: 'activity.retourGame',
          component: () => import('@/views/activity/retourGame.vue'),
          meta: { title: '转游开红包' },
        },
      ],
    },
    // 通过黑喵获取微信信息进入工单页
    {
      path: '/activityLanding',
      name: 'activityLanding',
      component: () => import('@/views/activityLanding/index.vue'),
      meta: { title: '活动承接页' },
    },
    {
      path: '/worker-sn',
      name: 'worker.sn',
      component: () => import('@/views/workerSn/index.vue'),
    },
    // 2011.11.16新增 预览圈子和帖子详情页面 - 可在浏览器中打开 绕过微信授权逻辑 只可查看 不可发帖、不可点赞等
    {
      path: '/preview',
      name: 'preview',
      component: () => import('@/views/preview/index.vue'),
      children: [
        // {
        //   path: 'home',
        //   name: 'preview.home',
        //   component: () => import('@/views/preview/home.vue'),
        //   meta: { title: '游戏圈', bottomNav: false, keepAlive: true, isBack: false },
        // },
        {
          path: 'details',
          name: 'preview.details',
          component: () => import('@/views/preview/details.vue'),
          meta: { title: '帖子详情' },
        },
        {
          path: '996',
          name: 'preview.996',
          component: () => import('@/views/preview/996.vue'),
          meta: { title: '变态猫—996游戏推荐福利官' },
        },
      ],
    },
    // 太古商城
    {
      path: '/tgStore',
      name: 'tgStore',
      component: () => import('@/views/tgStore/index.vue'),
      children: [
        {
          path: 'store',
          name: 'tgStore.store',
          component: () => import('@/views/tgStore/store.vue'),
          meta: { title: '伏妖记-道具商城', allowNoAuth: true },
        },
        {
          path: 'cutUserStore',
          name: 'tgStore.cutUserStore',
          component: () => import('@/views/tgStore/cutUserStore.vue'),
          meta: { title: '伏妖记-道具商城', allowNoAuth: true },
        },
        {
          path: 'pay',
          name: 'tgStore.pay',
          component: () => import('@/views/tgStore/pay.vue'),
          meta: { title: '支付', allowNoAuth: true },
        },
        {
          path: 'record',
          name: 'tgStore.record',
          component: () => import('@/views/tgStore/record.vue'),
          meta: { title: '购买记录', allowNoAuth: true },
        },
        {
          path: 'home',
          name: 'tgStore.home',
          component: () => import('@/views/tgStore/home.vue'),
          meta: { title: '商城主页', allowNoAuth: true },
        },
      ],
    },
    // 注册抽奖
    {
      path: '/drawLot',
      name: 'drawLot',
      component: () => import('@/views/drawLot/index.vue'),
      meta: { title: '购买记录', allowNoAuth: true },
    },
    // 签到活动
    {
      path: '/signAct',
      name: 'signAct',
      component: () => import('@/views/signAct/index.vue'),
      meta: { suspend: 3 },
    },
    // 代金券活动
    {
      path: '/voucherAct',
      name: 'voucherAct',
      component: () => import('@/views/voucherAct/index.vue'),
      meta: { title: '代金券活动', keepAlive: true },
    },
    // 代金券活动 - 领取记录
    {
      path: '/voucherRecord',
      name: 'voucherRecord',
      component: () => import('@/views/voucherAct/record.vue'),
      meta: { title: '领取记录' },
    },
    // 限时福利
    {
      path: '/limitedAct',
      name: 'limitedAct',
      component: () => import('@/views/limitedAct/index.vue'),
      meta: { suspend: 4 },
    },
    // 53 客服
    {
      path: '/service',
      name: 'service',
      component: () => import('@/views/service/index.vue'),
      meta: { title: '变态猫—996游戏推荐福利官' },
    },
    // 没有匹配的路由时，跳转到游戏圈中心页
    { path: '*', redirect: '/gameCircleCenter/home' },
  ],
});

const isLocalDevBrowser = () => {
  const hostname = window.location.hostname;
  return process.env.NODE_ENV === 'development' && (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    /^10\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname) ||
    /^192\.168\./.test(hostname)
  );
};

const ensureLocalDevUser = () => {
  if (store.state.account.userInfo) return;
  const devUid = localStorage.getItem('forum_dev_uid') || '1';
  store.commit('account/CHANGE_USERINFO', {
    id: 0,
    game_user_id: devUid,
    wechat_name: '本地调试用户',
    forum_name: '本地调试用户',
    avatar: '',
  });
};

const safeDecodeURIComponent = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const getSearchParam = (name) => {
  const search = window.location.search.replace(/^\?/, '');
  if (!search) return '';
  const params = search.split('&');
  for (let i = 0; i < params.length; i++) {
    const item = params[i];
    const equalIndex = item.indexOf('=');
    const rawKey = equalIndex > -1 ? item.slice(0, equalIndex) : item;
    const rawValue = equalIndex > -1 ? item.slice(equalIndex + 1) : '';
    if (safeDecodeURIComponent(rawKey) === name) {
      return safeDecodeURIComponent(rawValue);
    }
  }
  return '';
};

// 微信 OAuth 回跳会把页面参数放进 state；承接页链接参数可能含有 =、&、+、% 等字符。
// 只解析单个参数并对 query key/value 分别编码，避免整段 decode 后 split 抛错或截断，
// 否则 beforeEach 异常未调用 next() 时会停在空路由视图，用户看到白屏。
// 判断是否为微信授权页跳转链接
const isAuthorizationRedirectUrl = () => !!getSearchParam('code');

// 处理微信授权页跳转链接, 获取code
const handleLocationSearch = () => ({
  code: getSearchParam('code'),
  state: getSearchParam('state'),
});

// 处理微信授权页跳转链接, 获取路由name与query
const handleState = (state = '') => {
  const stateValue = String(state || '');
  const splitFlag = '_HM_';
  const splitIndex = stateValue.indexOf(splitFlag);
  const path = {};
  const queryObj = {};
  const routeName = splitIndex > -1 ? stateValue.slice(0, splitIndex) : stateValue;
  const queryString = splitIndex > -1 ? stateValue.slice(splitIndex + splitFlag.length) : '';
  path.name = safeDecodeURIComponent(routeName);
  if (!path.name) return path;
  if (queryString) {
    const queryList = queryString.indexOf('&') > -1
      ? queryString.split('&')
      : queryString.split(splitFlag);
    queryList.forEach((item) => {
      if (!item) return;
      const equalIndex = item.indexOf('=');
      const rawKey = equalIndex > -1 ? item.slice(0, equalIndex) : item;
      const rawValue = equalIndex > -1 ? item.slice(equalIndex + 1) : '';
      const key = safeDecodeURIComponent(rawKey);
      if (!key) return;
      queryObj[key] = safeDecodeURIComponent(rawValue);
    });
  }
  path.query = queryObj;
  return path;
};

// 将query对象转成"&"拼接的字符串，放入微信 state 前需要保留特殊字符。
const handleQuery = (query) => {
  let queryArr = [];
  for (let key in query) {
    const value = query[key] === undefined || query[key] === null ? '' : query[key];
    queryArr.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return queryArr.join('&');
};

const ACTIVITY_LANDING_AUTH_ROUTE = 'activityLanding';
// 记录本轮承接页授权跳转，用于 iOS 回跳后识别短时间内的重复授权。
const WX_AUTH_REDIRECT_RECORD_KEY = 'wx_auth_redirect_record';
// iOS 硬跳转前临时保存授权 token，避免 token 放在 hash query 里被 VueRouter 将 + 等字符解码变形。
const WX_AUTH_TOKEN_FALLBACK_KEY = 'wx_auth_token_fallback';
// 防重复授权的保护窗口，避免 iOS WebView 同一链接短时间内反复拉起 OAuth。
const WX_AUTH_REDIRECT_BLOCK_TIME = 60;
// 同一承接页目标最多自动补授权一次，避免接口异常或存储异常时重新进入授权死循环。
const WX_AUTH_REDIRECT_RETRY_LIMIT = 1;
// 承接页授权失败兜底标记，命中后不再自动拉授权，由页面展示失败提示。
const ACTIVITY_LANDING_AUTH_FAILED_QUERY = 'auth_failed';

// iOS 微信 WebView 在 OAuth 回跳后偶发重新取到旧 hash/history，导致承接页再次判断无 token 并拉起授权。
// 下面的记录、硬替换和 query token 兜底只服务 activityLanding，避免影响项目已有页面的授权恢复流程。
// 将 router.resolve 的 hash 路由结果转成完整 URL，供 location.replace / history.replaceState 使用。
const getRouteFullUrl = (route) => {
  const href = router.resolve(route).href || '';
  return `${window.location.protocol}//${window.location.host}${href.indexOf('/') === 0 ? href : `/${href}`}`;
};

// 生成当前承接页授权唯一标识，用 route name + query 判断是不是同一次授权回跳。
const getAuthRouteKey = (route) => `${route.name || ''}?${handleQuery(route.query || {})}`;

// 读取本轮授权跳转记录；sessionStorage 不可用时按无记录处理，避免阻塞正常授权。
const getWxAuthRedirectRecord = () => {
  try {
    return JSON.parse(sessionStorage.getItem(WX_AUTH_REDIRECT_RECORD_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

// 发起承接页 OAuth 前记录一次目标路由，后续用于识别 iOS 是否在同一页面反复拉授权。
const setWxAuthRedirectRecord = (route, retryCount = 0) => {
  if (!route || route.name !== ACTIVITY_LANDING_AUTH_ROUTE) return;
  try {
    sessionStorage.setItem(WX_AUTH_REDIRECT_RECORD_KEY, JSON.stringify({
      key: getAuthRouteKey(route),
      timestamp: moment().unix(),
      retryCount,
    }));
  } catch (error) {}
};

// 授权成功或承接页已拿到 token 后清理记录，避免影响下一次新的承接页访问。
const clearWxAuthRedirectRecord = () => {
  try {
    sessionStorage.removeItem(WX_AUTH_REDIRECT_RECORD_KEY);
  } catch (error) {}
};

// 授权成功后临时记录 token；只作为 iOS 承接页硬跳转后的兜底来源。
const setActivityLandingTokenFallback = (token) => {
  if (!token) return false;
  try {
    sessionStorage.setItem(WX_AUTH_TOKEN_FALLBACK_KEY, token);
    return true;
  } catch (error) {
    return false;
  }
};

// 读取 iOS 承接页硬跳转前缓存的 token，sessionStorage 不可用时返回空串。
const getActivityLandingTokenFallback = () => {
  try {
    return sessionStorage.getItem(WX_AUTH_TOKEN_FALLBACK_KEY) || '';
  } catch (error) {
    return '';
  }
};

// token 成功写入 localStorage 后立刻清掉临时副本，避免下次访问误用旧授权。
const clearActivityLandingTokenFallback = () => {
  try {
    sessionStorage.removeItem(WX_AUTH_TOKEN_FALLBACK_KEY);
  } catch (error) {}
};

// iOS 承接页短时间内再次命中同一路由时，说明可能进入了 OAuth 重入窗口。
const shouldBlockRepeatWxAuth = (route) => {
  if (!getDevice().ios || route.name !== ACTIVITY_LANDING_AUTH_ROUTE) return false;
  const record = getWxAuthRedirectRecord();
  return record.key === getAuthRouteKey(route)
    && moment().unix() - Number(record.timestamp || 0) < WX_AUTH_REDIRECT_BLOCK_TIME;
};

// 读取同一承接页目标已经自动补授权的次数，超出后停止自动授权，避免 iOS 循环拉起授权。
const getWxAuthRedirectRetryCount = (route) => {
  const record = getWxAuthRedirectRecord();
  if (!route || record.key !== getAuthRouteKey(route)) return 0;
  return Number(record.retryCount || 0);
};

// iOS 承接页授权成功后用完整 URL 硬替换，绕开微信 WebView 对 hash/history 的偶发重入。
const shouldHardReplaceAfterAuth = (route) => getDevice().ios && route.name === ACTIVITY_LANDING_AUTH_ROUTE;

// 提取承接页 URL 上的 token 兜底字段，兼容 access_token 和 token 两种参数名。
const getRouteAccessToken = (route) => {
  const query = (route && route.query) || {};
  const token = query.access_token || query.token || '';
  return safeDecodeURIComponent(Array.isArray(token) ? token[0] : token);
};

// token 只用于进入守卫时兜底缓存，缓存后要从 URL 移除，避免继续暴露在地址栏。
const getQueryWithoutAuthToken = (query = {}) => {
  const nextQuery = { ...query };
  delete nextQuery.access_token;
  delete nextQuery.token;
  return nextQuery;
};

// iOS 硬跳转时额外把 token 双重编码后短暂放到承接页 URL，sessionStorage 丢失时仍能恢复。
const getActivityLandingRouteWithToken = (route, token) => ({
  name: route.name,
  query: {
    ...(route.query || {}),
    access_token: encodeURIComponent(token),
  },
});

// 授权失败时给承接页带一个兜底标记，守卫和页面都会据此停止再次自动拉授权。
const getActivityLandingAuthFailedRoute = (route) => ({
  name: route.name,
  query: {
    ...(route.query || {}),
    [ACTIVITY_LANDING_AUTH_FAILED_QUERY]: '1',
  },
});

// 只在承接页识别授权失败标记，避免影响其它页面已有授权流程。
const isActivityLandingAuthFailedRoute = (route) => (
  route
  && route.name === ACTIVITY_LANDING_AUTH_ROUTE
  && route.query
  && route.query[ACTIVITY_LANDING_AUTH_FAILED_QUERY] === '1'
);

// 仅承接页使用 query/session token 兜底；其它页面不走该分支，保持原有授权流程不变。
const cacheActivityLandingRouteAccessToken = (route) => {
  if (!route || route.name !== ACTIVITY_LANDING_AUTH_ROUTE) return false;
  const token = getRouteAccessToken(route) || getActivityLandingTokenFallback();
  if (!token) return false;
  try {
    localStorage.setItem('access_token', token);
    clearActivityLandingTokenFallback();
    return true;
  } catch (error) {
    return setActivityLandingTokenFallback(token);
  }
};

// 以下页面不需要获取通用用户信息。
// activityLanding 仍需要微信授权拿 access_token，但授权后由承接页自己的 user_info 接口取页面/用户数据；
// 如果这里再调通用 /v1/user/get_info，接口偶发 token 异常会清 token + reload，导致重新 OAuth 循环。
const NO_USERINFO_LIST = ['activity.vouchers', 'activity.seckill', 'activityLanding'];
// 以下页面不需要授权
const NO_AUTHORIZATION_LIST = ['activity.authorization', 'activity.seckill'];
// 以下页面不走微信逻辑
const NO_VX_LIST = ['preview.996', 'preview.home', 'preview.details'];

// 2021.11.18新增微信授权登录埋点
const wxPoint = [
  { type: 1, from: 'gameCenter.home' },
  { type: 2, from: 'giftCenter.home' },
  { type: 3, from: 'gameCircle.home' },
  { type: 3, from: 'gameCircle.details' },
];

router.beforeEach(async (to, from, next) => {
  if (isLocalDevBrowser()) {
    ensureLocalDevUser();
    ensureForumUidForRoute(to);
    next();
    return;
  }

  if (NO_VX_LIST.includes(to.name)) {
    next();
    return;
  }

  if (!isWeiXin() && to.meta.allowNoAuth) {
    next();
    return;
  }

  // 承接页授权已确认失败时直接放行到页面兜底提示，不再自动 OAuth，避免失败态循环拉起授权。
  if (isActivityLandingAuthFailedRoute(to)) {
    clearWxAuthRedirectRecord();
    clearActivityLandingTokenFallback();
    next();
    return;
  }

  // iOS 承接页授权后可能通过完整 URL 或 session 兜底带回 token，先在守卫层缓存，避免还没进组件就再次拉授权。
  const shouldCleanActivityLandingTokenQuery = !!getRouteAccessToken(to);
  if (cacheActivityLandingRouteAccessToken(to)) {
    clearWxAuthRedirectRecord();
    if (shouldCleanActivityLandingTokenQuery) {
      next({
        name: to.name,
        query: getQueryWithoutAuthToken(to.query),
        replace: true,
      });
    } else {
      next();
    }
    return;
  }

  const { userInfo } = store.state.account;
  try {
    if (userInfo) {
      // vuex中存在用户信息1
      ensureForumUidForRoute(to);
      next();
    } else {
      if (isAuthorizationRedirectUrl()) {
        // 处理微信授权页跳转url获取code、pathName、pathQuery，并使用处理过的url替换历史记录中的url
        // ?code=071ooabY1ZDee01tmA9Y1tsUaY1ooabb&state=gameCircle.home_HM_circleId%3D3
        const { code, state } = handleLocationSearch();
        const pathObj = handleState(state) || {};
        if (!code || !pathObj.name) {
          next({ name: 'gameCenter.home', replace: true });
          return;
        }
        const targetRoute = { name: pathObj.name, query: pathObj.query || {} };
        const isActivityLandingAuthTarget = pathObj.name === ACTIVITY_LANDING_AUTH_ROUTE;
        // 非承接页继续沿用旧逻辑：先清理成首页地址，再通过 router.next 恢复目标页。
        if (!isActivityLandingAuthTarget) {
          const _url = window.location.protocol + '//' + window.location.host + '/#/gameCenter/home';
          window.history.replaceState({}, 0, _url);
        }
        // 通过code获取access_token，本地缓存跳转路由、access_token、授权时间
        const fromPath = pathObj.name;
        const fromFilter = wxPoint.filter((m) => m.from === fromPath)[0];
        const wxObj = fromFilter ? { code, type: fromFilter.type } : { code };
        let authRes = null;
        try {
          authRes = await getAccessToken(wxObj);
        } catch (error) {
          // 承接页 code 兑换 token 失败时打失败标记回目标页，避免 iOS 来回拉授权。
          if (isActivityLandingAuthTarget) {
            window.location.replace(getRouteFullUrl(getActivityLandingAuthFailedRoute(targetRoute)));
            return;
          }
          throw error;
        }
        const accessToken = authRes && authRes.data && authRes.data.token;
        // 承接页授权返回为空 token 时同样进入失败兜底，不再重进 OAuth。
        if (isActivityLandingAuthTarget && !accessToken) {
          window.location.replace(getRouteFullUrl(getActivityLandingAuthFailedRoute(targetRoute)));
          return;
        }
        localStorage.setItem('redirectRoute', JSON.stringify(pathObj));
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('timestamp', moment().unix());
        // 非 iOS 承接页只需要把地址清成目标页，避免 URL 保留 code/state 后被二次消费。
        if (isActivityLandingAuthTarget && !shouldHardReplaceAfterAuth(pathObj)) {
          window.history.replaceState({}, 0, getRouteFullUrl(targetRoute));
        }
        if (!shouldHardReplaceAfterAuth(pathObj)) {
          clearWxAuthRedirectRecord();
        }
        // iOS 承接页用完整 URL 硬跳转，并用 session + 短暂 URL 参数双兜底带回 token。
        if (shouldHardReplaceAfterAuth(pathObj)) {
          setActivityLandingTokenFallback(accessToken);
          window.location.replace(getRouteFullUrl(getActivityLandingRouteWithToken(targetRoute, accessToken)));
          return;
        }
        // 判断授权页跳转url中state是否存在
        if (state) {
          next({ ...targetRoute, replace: true });
        } else {
          next({ name: 'gameCenter.home', replace: true });
        }
        return;
      }
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        // 如果本地缓存中有access_token
        try {
          if (!NO_USERINFO_LIST.includes(to.name)) {
            const r = await getUserInfo();
            store.commit('account/CHANGE_USERINFO', r.data);
            ensureForumUidForRoute(to);
            next();
          } else {
            next();
          }
        } catch (error) {
          // 如果通过access_token获取用户信息失败（access_token过期）则清空缓存，重新走授权拿到新的token
          console.error('token error');
          localStorage.clear();
          next();
          // next({ name: 'gameCenter.home', replace: true });
        }
      } else {
        if (NO_AUTHORIZATION_LIST.includes(to.name)) {
          next();
          return;
        }
        let wxAuthRetryCount = 0;
        // iOS 承接页短时间内重复命中同一授权目标时，先恢复 token；恢复不到则最多补授权一次。
        if (shouldBlockRepeatWxAuth(to)) {
          if (cacheActivityLandingRouteAccessToken(to)) {
            clearWxAuthRedirectRecord();
            next();
            return;
          }
          const retryCount = getWxAuthRedirectRetryCount(to);
          if (retryCount >= WX_AUTH_REDIRECT_RETRY_LIMIT) {
            clearWxAuthRedirectRecord();
            next({
              ...getActivityLandingAuthFailedRoute(to),
              replace: true,
            });
            return;
          }
          wxAuthRetryCount = retryCount + 1;
        }
        // 如果本地缓存中没有accessToken,则需要通过微信授权拿到这个access_token
        let redirect_uri = encodeURIComponent(window.location.origin);
        // 如果本地缓存没有access_token, 则去开放平台拿code，通过code获取access_token
        const query = to.query ? handleQuery(to.query) : '';
        const state = query ? `${to.name}_HM_${query}` : to.name;
        const authUrl =
          `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirect_uri}`
          + `&response_type=code&scope=snsapi_userinfo&state=${encodeURIComponent(state)}#wechat_redirect`;
        // 只记录承接页授权目标；其它页面 setWxAuthRedirectRecord 内部会直接忽略。
        setWxAuthRedirectRecord(to, wxAuthRetryCount);
        window.location.href = authUrl;
      }
    }
  } catch (error) {
    console.log('未知异常', error);
    next({ name: 'gameCenter.home', replace: true });
  }
});

router.afterEach((to, from) => {
  syncForumRouteTitle(to);

  if (isLocalDevBrowser()) {
    return;
  }

  if (NO_VX_LIST.includes(to.name)) {
    return;
  }
  if (NO_AUTHORIZATION_LIST.includes(from.name)) {
    // next();
    return;
  }

  if (!isWeiXin() && to.meta.allowNoAuth) {
    // next();
    return;
  }

  // 同一个url调用一次config接口注入权限验证配置
  wxConfig();
  if (!NO_USERINFO_LIST.includes(to.name)) {
    // 用户论坛封禁需要在切换页面的时候获取用户信息
    // store.dispatch('account/getUserInfo');
    // const { userInfo } = store.state.account;
    // const prohibition = userInfo.prohibition;
    // if (prohibition.state_type == 1) {
    //   let warnMsg = `您因违规操作而被封号\n 封号时间：${
    //     prohibition.time_type == 1 ? '永久' : prohibition.start_time + '~' + prohibition.end_time
    //   }`;
    //   Dialog.alert({
    //     title: '温馨提示',
    //     message: warnMsg,
    //   }).then(() => {
    //     wx.closeWindow();
    //   });
    //   return;
    // }
  }
  // 添加白名单，白名单中的页面属于论坛页面，其余都属于平台页面
  const WHITE_LIST = ['gameCircle.home', 'gameCircle.details']; // 游戏圈
  let deviceType = 3; // 默认是电脑
  if (getDevice().Mobile) {
    deviceType = 1;
  }
  if (getDevice().iPad) {
    deviceType = 2;
  }
  if (
    WHITE_LIST.find((item) => {
      return to.name == item;
    })
  ) {
    let forumHasLogin = sessionStorage.getItem('forumHasLogin');
    if (forumHasLogin) return;
    sessionStorage.setItem('forumHasLogin', true);
    // 论坛上报
    reportLoginForum({ type: deviceType });
  } else {
    let platHasLogin = sessionStorage.getItem('platHasLogin');
    if (platHasLogin) return;
    sessionStorage.setItem('platHasLogin', true);
    // 平台上报
    reportLoginPlatform({ type: deviceType });
  }

  // 未注册论坛用户进入注册页
  // if (
  //   userInfo.forum_user_id == 0 &&
  //   to.name != 'account.registeredAccount' &&
  //   !WHITE_LIST.find(item => {
  //     return to.name.includes(item);
  //   })
  // ) {
  //   localStorage.setItem(
  //     'redirectRoute',
  //     JSON.stringify({
  //       name: to.name,
  //       query: to.query,
  //     }),
  //   );
  //   router.replace({ name: 'account.registeredAccount' }).catch(err => err);
  // }
  // ios复制链接问题
  // if (getDevice().ios) {
  //   setTimeout(() => {
  //     const href = window.location.href;
  //     window.location.href = href;
  //   }, 200);
  // }
  // 最后一次登录数据上报
  // if (
  //   !WHITE_LIST.find(item => {
  //     return to.name.includes(item);
  //   })
  // ) {
  //   // 论坛页面
  //   reportLastVisitTime({ is_visit_forum: 1 });
  // } else {
  //   reportLastVisitTime();
  // }
});

export default router;
